import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { QuerySchema, FilterOperator } from './schema';
import { parseQuery, generateFilterId, type ActiveFilter, type ParsedQueryState } from './parser';
import { serializeQuery } from './serializer';

export interface UseQueryFilterOptions {
  query: string;
  schema?: QuerySchema<string>;
  onSearch: (query: string) => void;
  debounceMs?: number;
}

export function useQueryFilter({
  query,
  schema,
  onSearch,
  debounceMs = 0,
}: UseQueryFilterOptions) {
  const [bareText, setBareTextState] = useState('');
  const [filters, setFilters] = useState<ActiveFilter[]>([]);
  const isInternalUpdate = useRef(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync state when external query prop changes
  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    const parsed = parseQuery(query, schema);
    setBareTextState(parsed.bareText);
    setFilters(parsed.filters);
  }, [query, schema]);

  // Compute canonical query string from current state
  const currentQueryString = useMemo(() => {
    return serializeQuery({ bareText, filters }, schema);
  }, [bareText, filters, schema]);

  // Trigger search update to parent
  const triggerSearch = useCallback(
    (newQuery: string) => {
      isInternalUpdate.current = true;
      if (debounceMs > 0) {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
          onSearch(newQuery);
        }, debounceMs);
      } else {
        onSearch(newQuery);
      }
    },
    [onSearch, debounceMs]
  );

  const setBareText = useCallback(
    (text: string) => {
      // Check if text contains structured syntax (e.g. key:val)
      const parsed = parseQuery(text, schema);
      if (parsed.filters.length > 0) {
        // Smart extraction: extract syntax filters into chips
        setFilters((prev) => {
          const merged = [...prev];
          for (const newF of parsed.filters) {
            const existingIdx = merged.findIndex((f) => f.key === newF.key);
            if (existingIdx >= 0) {
              merged[existingIdx] = newF;
            } else {
              merged.push(newF);
            }
          }
          const nextQuery = serializeQuery({ bareText: parsed.bareText, filters: merged }, schema);
          triggerSearch(nextQuery);
          return merged;
        });
        setBareTextState(parsed.bareText);
      } else {
        setBareTextState(text);
        const nextQuery = serializeQuery({ bareText: text, filters }, schema);
        triggerSearch(nextQuery);
      }
    },
    [schema, filters, triggerSearch]
  );

  const addFilter = useCallback(
    (
      key: string,
      value: string,
      operator: FilterOperator = '=',
      values?: string[],
      highValue?: string
    ) => {
      setFilters((prev) => {
        const next = prev.filter((f) => f.key !== key);
        const newFilter: ActiveFilter = {
          id: generateFilterId(key),
          key,
          operator,
          value,
          values,
          highValue,
        };
        next.push(newFilter);
        const nextQuery = serializeQuery({ bareText, filters: next }, schema);
        triggerSearch(nextQuery);
        return next;
      });
    },
    [bareText, schema, triggerSearch]
  );

  const updateFilter = useCallback(
    (id: string, updates: Partial<ActiveFilter>) => {
      setFilters((prev) => {
        const next = prev.map((f) => (f.id === id ? { ...f, ...updates } : f));
        const nextQuery = serializeQuery({ bareText, filters: next }, schema);
        triggerSearch(nextQuery);
        return next;
      });
    },
    [bareText, schema, triggerSearch]
  );

  const removeFilter = useCallback(
    (id: string) => {
      setFilters((prev) => {
        const next = prev.filter((f) => f.id !== id);
        const nextQuery = serializeQuery({ bareText, filters: next }, schema);
        triggerSearch(nextQuery);
        return next;
      });
    },
    [bareText, schema, triggerSearch]
  );

  const clearAll = useCallback(() => {
    setBareTextState('');
    setFilters([]);
    triggerSearch('');
  }, [triggerSearch]);

  return {
    bareText,
    filters,
    queryString: currentQueryString,
    setBareText,
    addFilter,
    updateFilter,
    removeFilter,
    clearAll,
    hasActiveFilters: filters.length > 0 || !!bareText.trim(),
  };
}
